/**
 * Everything about one file.
 *
 * SHAPE
 *   Preview on top, then provenance, then discussion. That order is the order
 *   the questions get asked: what is this, who changed it, what did we decide.
 *
 *   When a file cannot be rendered — every SolidWorks format, because no free
 *   browser renderer exists — we say so plainly and offer its COMPANIONS: the
 *   exported PDF or STEP of the same part. Pretending to preview a .SLDPRT
 *   would be worse than admitting we cannot.
 */
import { Suspense, lazy, useMemo, useState, useEffect } from 'react'
import { Download, PencilLine, FileQuestion, ExternalLink, Copy, Check } from 'lucide-react'
import {
  previewKindOf, companionsOf, kindOf, labelForKind, basename,
  type FileEntry, type Path, type ProjectId,
} from '@lattice/core'
import { useWorkspace, filesByProject } from '@/store/workspace'
import { useUi } from '@/store/ui'
import { api } from '@/store/api'
import { ProvenanceTimeline } from './ProvenanceTimeline'
import { CommentPanel } from './Comments'
import { FileIcon } from './FileIcon'
import { LockControls } from './LockControls'
import { FileViewers } from './Presence'
import { Button, Badge, Tooltip, EmptyState, Spinner } from './ui/primitives'
import { ErrorBoundary } from './ErrorBoundary'
import { formatBytes, formatExact, formatRelative } from '@/lib/format'
import { cn } from '@/lib/utils'

const PdfPreview = lazy(() => import('./preview/PdfPreview').then(m => ({ default: m.PdfPreview })))
const StepPreview = lazy(() => import('./preview/StepPreview').then(m => ({ default: m.StepPreview })))

type Tab = 'preview' | 'history' | 'comments'

export function FileDetail({ projectId, path, onRename }: {
  projectId: ProjectId
  path: string
  onRename: (path: string, name: string) => void
}) {
  const changes = useWorkspace(s => s.changes)
  const [tab, setTab] = useState<Tab>('preview')

  const files = useMemo(() => filesByProject(changes).get(projectId), [changes, projectId])
  const entry = files?.get(path.toLowerCase())
  const allPaths = useMemo(() => (files ? [...files.values()].map(f => f.path) : []), [files])

  if (!entry) {
    return <EmptyState icon={FileQuestion} title="File not found" hint="It may have been deleted or renamed." />
  }

  const name = basename(entry.path)
  const kind = kindOf(entry.path)
  const previewKind = previewKindOf(entry.path)

  return (
    <div className="flex h-full min-w-0 flex-col bg-app">
      <header className="shrink-0 border-b border-[var(--border)] px-3 py-2">
        <div className="flex items-start gap-2">
          <FileIcon path={entry.path} className="mt-1 h-4 w-4" />
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold leading-tight" title={name}>{name}</h2>
            <p className="truncate text-xs text-[var(--fg-subtle)]" title={entry.path}>{entry.path}</p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <FileViewers projectId={projectId} path={entry.path} />
            <LockControls projectId={projectId} path={entry.path} />
            <Tooltip label="Rename" keys={['F2']}>
              <Button variant="ghost" size="icon" onClick={() => onRename(entry.path, name)} aria-label="Rename">
                <PencilLine className="h-3.5 w-3.5" strokeWidth={1.75} />
              </Button>
            </Tooltip>
            <Tooltip label="Download">
              <Button
                variant="ghost" size="icon" aria-label="Download"
                onClick={() => void import('@/lib/transfer').then(m => m.downloadFiles(projectId, [entry.path]))}
              >
                <Download className="h-3.5 w-3.5" strokeWidth={1.75} />
              </Button>
            </Tooltip>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--fg-subtle)]">
          <Badge>{labelForKind(kind, 1)}</Badge>
          <span className="tabular">{formatBytes(entry.size)}</span>
          <span>rev {entry.revisions}</span>
          <Tooltip label={formatExact(entry.updatedAt)}>
            <span className="cursor-help">updated {formatRelative(entry.updatedAt)}</span>
          </Tooltip>
          <HashChip sha={entry.sha} />
        </div>

        <nav className="mt-2 flex gap-0.5" role="tablist">
          {(['preview', 'history', 'comments'] as Tab[]).map(id => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={cn(
                'snap rounded-[var(--radius-sm)] px-2 py-1 text-xs font-medium capitalize',
                tab === id ? 'bg-[var(--bg-active)] text-[var(--fg)]' : 'text-[var(--fg-muted)] hover:bg-[var(--bg-hover)]',
              )}
            >
              {id}
            </button>
          ))}
        </nav>
      </header>

      <div className="min-h-0 flex-1 overflow-hidden">
        {tab === 'preview' && (
          <Preview projectId={projectId} entry={entry} previewKind={previewKind} allPaths={allPaths} />
        )}
        {tab === 'history' && (
          <div className="h-full overflow-y-auto">
            <ProvenanceTimeline projectId={projectId} path={entry.path} />
          </div>
        )}
        {tab === 'comments' && <CommentPanel projectId={projectId} path={entry.path} />}
      </div>
    </div>
  )
}

/** The content hash, copyable — engineers paste these into emails to vendors. */
function HashChip({ sha }: { sha: string }) {
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    if (!copied) return
    const t = setTimeout(() => setCopied(false), 1400)
    return () => clearTimeout(t)
  }, [copied])
  return (
    <Tooltip label={copied ? 'Copied' : <span className="font-mono">{sha}</span>}>
      <button
        onClick={() => { void navigator.clipboard?.writeText(sha); setCopied(true) }}
        className="snap inline-flex items-center gap-1 font-mono tabular hover:text-[var(--fg)]"
      >
        {copied ? <Check className="h-3 w-3" strokeWidth={2} /> : <Copy className="h-3 w-3" strokeWidth={1.75} />}
        {sha.slice(0, 10)}
      </button>
    </Tooltip>
  )
}

/**
 * Fetch a short-lived URL for this content and hand it to the right viewer.
 * The URL is re-requested when the file changes, never cached across files.
 */
function Preview({ projectId, entry, previewKind, allPaths }: {
  projectId: ProjectId
  entry: FileEntry
  previewKind: ReturnType<typeof previewKindOf>
  allPaths: Path[]
}) {
  const { open } = useUi()
  const [url, setUrl] = useState<string>()
  const [error, setError] = useState<string>()

  useEffect(() => {
    if (previewKind === 'none') return
    let cancelled = false
    setUrl(undefined)
    setError(undefined)
    api.downloadUrls(projectId, [entry.path])
      .then(r => {
        if (cancelled) return
        const item = r.items[0]
        if (item?.url) setUrl(item.url)
        else setError(item?.reason ?? 'This file has no stored content.')
      })
      .catch(e => { if (!cancelled) setError((e as Error).message) })
    return () => { cancelled = true }
  }, [projectId, entry.path, entry.sha, previewKind])

  if (previewKind === 'none') {
    const companions = companionsOf(entry.path, allPaths)
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <FileIcon path={entry.path} className="h-8 w-8" />
        <div>
          <p className="text-sm font-medium">No preview for {basename(entry.path).split('.').pop()} files</p>
          <p className="mt-1 max-w-sm text-sm text-[var(--fg-subtle)]">
            SolidWorks formats are proprietary and cannot be rendered in a browser without
            licensed software. Everything else about this file is here.
          </p>
        </div>
        {companions.length > 0 && (
          <div className="w-full max-w-sm">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--fg-subtle)]">
              Viewable exports of this part
            </p>
            <div className="space-y-0.5">
              {companions.map(companion => (
                <button
                  key={companion}
                  onClick={() => open(companion)}
                  className="snap flex w-full items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border)] px-2 py-1.5 text-left text-sm hover:bg-[var(--bg-hover)]"
                >
                  <FileIcon path={companion} />
                  <span className="min-w-0 flex-1 truncate">{basename(companion)}</span>
                  <ExternalLink className="h-3 w-3 shrink-0 text-[var(--fg-subtle)]" strokeWidth={1.75} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  if (error) return <EmptyState icon={FileQuestion} title="Cannot preview this file" hint={error} />
  if (!url) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-[var(--fg-subtle)]">
        <Spinner className="mr-2" /> Preparing preview…
      </div>
    )
  }

  const name = basename(entry.path)
  return (
    <ErrorBoundary what="This preview" resetKey={entry.sha}>
    <Suspense fallback={<div className="flex h-full items-center justify-center"><Spinner /></div>}>
      {previewKind === 'pdf' && <PdfPreview url={url} name={name} className="h-full" />}
      {previewKind === 'step' && <StepPreview url={url} name={name} size={entry.size} className="h-full" />}
      {previewKind === 'image' && (
        <div className="flex h-full items-center justify-center overflow-auto bg-[var(--bg-inset)] p-4">
          <img src={url} alt={name} className="max-h-full max-w-full object-contain" />
        </div>
      )}
      {previewKind === 'text' && <TextPreview url={url} />}
    </Suspense>
    </ErrorBoundary>
  )
}

function TextPreview({ url }: { url: string }) {
  const [text, setText] = useState<string>()
  useEffect(() => {
    let cancelled = false
    // Cap the read: a 400 MB text file must not take the tab down.
    fetch(url).then(r => r.text()).then(t => { if (!cancelled) setText(t.slice(0, 500_000)) }).catch(() => undefined)
    return () => { cancelled = true }
  }, [url])
  if (text === undefined) return <div className="flex h-full items-center justify-center"><Spinner /></div>
  return (
    <pre className="h-full overflow-auto bg-[var(--bg-inset)] p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap">
      {text}
    </pre>
  )
}
